import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { getRequesterCandidate, type RequesterCandidate } from "@/lib/people";
import { RequesterPicker } from "@/components/RequesterPicker";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  FileText,
  Lock,
  Search,
  Send,
  Trash2,
  UserPlus,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { AssistantPanel } from "@/components/AssistantPanel";
import { PendingFilesField } from "@/components/PendingFilesField";
import {
  PendingParticipantsField,
  type InvitablePerson,
} from "@/components/PendingParticipantsField";
import { Feature, useSession } from "@/components/session-provider";
import { SiteCombobox } from "@/components/SiteCombobox";
import { formatPhone, normaliseExtension, normalisePhone } from "@/lib/phone";
import { clearRequestDraft, readRequestDraft, writeRequestDraft } from "@/lib/request-draft";
import { formatSite } from "@/lib/sites";
import {
  addParticipant,
  createTicket,
  formatBytes,
  getReference,
  uploadAttachment,
  type Reference,
  type ReferenceCategory,
  type ReferenceTeam,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/new")({
  // Set by the people screen's "raise a request for them". The server refuses unless this
  // caller may create tickets beyond their own, so an arbitrary id in the URL grants
  // nothing — it only pre-fills who the request is for.
  validateSearch: (search: Record<string, unknown>): { forUser?: string } => {
    const forUser = typeof search.forUser === "string" ? search.forUser : undefined;
    return forUser ? { forUser } : {};
  },
  component: NewRequestPage,
});

/**
 * The wizard, one question per screen.
 *
 * ⚠️ Team and category are two steps rather than one, and the split is the point. They used
 * to share a step: picking a team revealed a category list BELOW the team cards, which meant
 * the thing you had to do next appeared off the bottom of a phone screen, under content you
 * had just finished reading. The commonest outcome was pressing Continue — disabled, because
 * no category was chosen — and having nothing on screen explain why. Splitting them also
 * gives the category list room to be searchable, which it has to be: a team with thirty
 * categories is not a grid anybody reads.
 *
 * ⚠️ Steps carry a KEY, and every branch reads the key rather than the index. The list is
 * not the same length for everybody — somebody who may raise a request for a colleague gets
 * an extra step in front — so an index means two different things depending on who is
 * looking. Numbered comparisons would put the category list where the review belongs, for
 * exactly the population that can file under somebody else's name.
 */
const baseSteps = [
  { key: "team", label: "Which team?" },
  { key: "category", label: "What is it about?" },
  { key: "details", label: "Tell us more" },
  { key: "review", label: "Review" },
] as const;

const requesterStep = { key: "requester", label: "Who is this for?" } as const;

type StepKey = (typeof baseSteps)[number]["key"] | typeof requesterStep.key;

/**
 * Marks a field as required, for eyes and for screen readers alike.
 *
 * An asterisk alone is a convention people have to already know, and it is invisible to
 * anybody listening rather than looking — so the word travels with it and only the symbol
 * is drawn.
 */
function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden>
      *<span className="sr-only"> required</span>
    </span>
  );
}

/**
 * What is wrong with one field, under that field.
 *
 * ⚠️ `role="alert"` so the message is announced when it appears rather than sitting there
 * silently for anybody not looking at that part of the screen. The element is rendered only
 * when there is something to say — an empty live region that fills in later is announced
 * inconsistently across screen readers.
 */
function FieldProblem({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;

  return (
    <p id={id} role="alert" className="mt-1.5 text-xs text-destructive">
      {message}
    </p>
  );
}

function NewRequestPage() {
  const navigate = useNavigate();
  const { hasFeature, user, refresh } = useSession();
  const { forUser } = Route.useSearch();

  // Who the request is for, when somebody is filing it on another person's behalf. Their
  // name is looked up purely so the form can say whose request this will be — filing one
  // "for" a person without naming them on the screen is how the wrong person gets a ticket.
  const [onBehalfOf, setOnBehalfOf] = useState<RequesterCandidate | null>(null);
  const [onBehalfOfUnknown, setOnBehalfOfUnknown] = useState(false);

  useEffect(() => {
    if (!forUser) {
      setOnBehalfOf(null);
      setOnBehalfOfUnknown(false);
      return;
    }

    let cancelled = false;

    // ⚠️ The requester-candidate lookup, NOT `/api/people`. That endpoint is fenced to
    // `user.manage` wholesale, so resolving the name through it refused exactly the desk
    // agents this feature exists for — they could be handed a `?forUser=` link and then be
    // told the person could not be looked up. This one is gated on the same expression as
    // the create itself, so whoever may file for somebody can also name them.
    void getRequesterCandidate(forUser)
      .then((p) => !cancelled && setOnBehalfOf(p))
      // ⚠️ Recorded rather than swallowed. An id the screen cannot put a name to must stop
      // the form, or it files a request against a colleague nobody on this screen saw.
      .catch(() => !cancelled && setOnBehalfOfUnknown(true));

    return () => {
      cancelled = true;
    };
  }, [forUser]);

  /**
   * Whether this form is about to hand somebody else's name to a request.
   *
   * ⚠️ `forUser` arrives in the URL, which means it survives a bookmark, the back button and
   * the address bar's autocomplete. Somebody who once raised a request for a colleague can
   * return to that URL days later and file their OWN problem under that colleague's name —
   * the request becomes theirs, they are notified about it, and it counts as theirs. So the
   * name is repeated at the point of submission rather than stated once at the top, and the
   * button says whose request this will be.
   */
  const raisingForSomeoneElse = Boolean(forUser);

  // Rendering only. The endpoint checks the same flag itself and 404s when it is off.
  const aiEnabled = hasFeature(Feature.AiAssistant);

  // Same rule, same reason. Whether the upload will actually succeed also depends on
  // storage being configured, which cannot be known until there is a ticket to ask
  // about — so that one surfaces as a named failure after submitting rather than as a
  // hidden control before it.
  const attachmentsEnabled = hasFeature(Feature.TicketAttachments);

  const [reference, setReference] = useState<Reference | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [team, setTeam] = useState<ReferenceTeam | null>(null);
  const [category, setCategory] = useState<ReferenceCategory | null>(null);

  /**
   * What has been typed into the category search.
   *
   * Cleared whenever the team changes, so coming back and choosing a different team does
   * not show its categories already filtered by a word somebody typed for the last one —
   * which reads as "this team has almost nothing in it".
   */
  const [categorySearch, setCategorySearch] = useState("");

  /**
   * The answer to "who is this for", when that step is shown at all.
   *
   * ⚠️ Three states, not two. "For me" and "not answered yet" are both the absence of
   * `forUser`, and they must not look the same to Continue — without the distinction the
   * step can be walked straight past, and the deliberate choice it exists to force stops
   * being deliberate. "Somebody else, but not yet named" is the third: it is what reveals
   * the search, and it is NOT enough to continue on.
   */
  const [explicitRequesterChoice, setExplicitRequesterChoice] = useState<"me" | "other" | null>(
    null,
  );

  /**
   * ⚠️ Derived, so a `?forUser=` deep link arrives already on "somebody else" without an
   * effect to synchronise. The People panel's "raise a request for them" lands here with the
   * param already set, and a screen that showed neither option chosen would be asking a
   * question it had itself already answered.
   */
  const requesterChoice: "me" | "other" | null =
    explicitRequesterChoice ?? (forUser ? "other" : null);

  const setRequesterChoice = setExplicitRequesterChoice;
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [siteId, setSiteId] = useState("");

  /**
   * Where the service desk should ring about this request.
   *
   * ⚠️ Pre-filled from the person's own profile, and only for a request they are raising
   * for THEMSELVES. On an on-behalf-of ticket the number belongs to the colleague the
   * request is about, and starting that field with the agent's own desk line is how a
   * callback gets logged against the wrong person and nobody notices — the field is
   * already filled, so nobody reads it.
   */
  const [contactPhone, setContactPhone] = useState(
    forUser ? "" : formatPhone(user?.contact.contactPhone),
  );
  const [contactExtension, setContactExtension] = useState(
    forUser ? "" : (user?.contact.contactPhoneExtension ?? ""),
  );

  const [files, setFiles] = useState<File[]>([]);

  /** People to put on the request once it exists. Invited after creation, like the files. */
  const [participants, setParticipants] = useState<InvitablePerson[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitStage, setSubmitStage] = useState<"creating" | "attaching" | "inviting">("creating");
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * The request that was just raised, once there is one.
   *
   * ⚠️ BOTH numbers, and this used to hold only the opaque one — which the confirmation
   * screen then printed as "your reference". D-35 is explicit that `#412` is the identifier
   * people see and that the opaque number appears on ticket detail alone, so a requester was
   * finishing the form, being handed a value nobody's queue displays, and quoting it to the
   * desk — while every email they went on to receive said `#412`.
   */
  const [created, setCreated] = useState<{ number: string; displayNumber: number } | null>(null);

  /**
   * Files that did not make it, and why, reported on the confirmation screen.
   *
   * ⚠️ The request itself succeeded in every case here. A file is only validated when it
   * is actually sent — the server reads its bytes, not its name — so a rejection can only
   * land after the ticket exists. Saying "your request was not submitted" because an
   * attachment bounced would be a lie that sends someone to raise it a second time.
   */
  const [attachmentFailures, setAttachmentFailures] = useState<{ name: string; reason: string }[]>(
    [],
  );
  const [attachedCount, setAttachedCount] = useState(0);

  /**
   * People who were chosen but could not be added, and why.
   *
   * ⚠️ Same rule as the files above: the request itself succeeded in every case. An
   * invitation is authorised against the ticket, so a refusal — no clearance for the
   * category, a colleague at another site — can only land after it exists. Reporting it as
   * "your request was not submitted" would send somebody to raise it a second time.
   */
  const [participantFailures, setParticipantFailures] = useState<
    { name: string; reason: string }[]
  >([]);
  const [invitedCount, setInvitedCount] = useState(0);

  /**
   * One key per form, not per attempt.
   *
   * A retry after a timeout must carry the SAME key or the server cannot tell a retry
   * from a second request — which is precisely when a user retries, and precisely when a
   * duplicate ticket is most confusing.
   */
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const data = await getReference();
        if (!cancelled) setReference(data);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof ApiError ? e.message : "Could not load the request form.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------------
  // The half-written request
  // -------------------------------------------------------------------------
  //
  // ⚠️ THIS FORM USED TO LOSE EVERYTHING TO ONE MIS-CLICK. Four steps, a description of up
  // to 4,000 characters (D-44), all of it component state — so the sidebar, the back button
  // or a reload took the lot, and somebody who had spent five minutes describing a problem
  // started again. That is the point at which people stop raising tickets and ring the desk.
  //
  // See `request-draft.ts` for why this is sessionStorage and what it deliberately does not
  // keep. It is a convenience and never a record: nothing has been submitted, and every value
  // is re-validated server-side on create exactly as though it had just been typed.

  /** True once a stored draft has been put back, so the screen can say so. */
  const [restoredDraft, setRestoredDraft] = useState(false);

  /** Restoring happens once. Without this it would fight every keystroke it just enabled. */
  const restoreAttempted = useRef(false);

  /**
   * Whether the restore has had its turn. Nothing may be SAVED before it has.
   *
   * ⚠️ State rather than the ref above, because the saving effect has to re-run once this
   * becomes true and a ref would not wake it. Without the gate the two effects race and the
   * saver wins: on first paint the form is empty, so it would write an empty draft — which
   * means deleting the stored one — a beat before the restorer got to read it. The feature
   * would then work perfectly in every test that did not involve leaving the page.
   */
  const [restoreSettled, setRestoreSettled] = useState(false);

  // Which way the next panel should travel. Forward slides in from the right, Back from
  // the left — the movement matches the direction of travel, which is what makes a
  // carousel legible rather than merely animated.
  const [direction, setDirection] = useState<1 | -1>(1);

  function goToStep(next: number) {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  }

  /**
   * Whether the number typed is one anybody could actually ring.
   *
   * ⚠️ A courtesy, not the control. The server refuses a create without a ringable number
   * and decides the stored format; this only means somebody finds out while they are still
   * looking at the field, rather than after pressing Submit on the last step.
   */
  const normalisedPhone = useMemo(() => normalisePhone(contactPhone), [contactPhone]);

  /**
   * The site already on the requester's account, if there is one.
   *
   * ⚠️ The REQUESTER's, not the caller's. On a request raised for somebody else the site is
   * theirs — filing a colleague's problem against the agent's own facility would route and
   * report it to a building the person has never been in.
   *
   * ⚠️ Read BEFORE the validation below rather than beside the field it draws, because it is
   * half the answer to "does this request have a site": one of this and the picker has to be
   * filled, and which one is not the person's choice.
   */
  const assignedSite = forUser ? (onBehalfOf?.siteName ?? null) : (user?.siteName ?? null);

  /**
   * What is wrong with each field, or null when nothing is.
   *
   * ⚠️ The BOUNDS MATCH THE SERVER'S — see `MinimumSubjectLength` and friends in
   * TicketEndpoints. A browser rule stricter than the API's turns a valid request away for
   * no reason; one looser lets somebody type a request the API then refuses, with a message
   * describing a rule this screen never showed them. Both are worse than agreeing.
   *
   * ⚠️ And this is not the control. Every one of these is re-checked server-side and
   * answered with a 400 that names the field. This exists so the answer arrives while the
   * cursor is still in the box.
   */
  const problems = useMemo(
    () => ({
      subject:
        subject.trim().length === 0
          ? "Tell us what this is about."
          : subject.trim().length < 5
            ? "A few more words — at least 5 characters."
            : null,

      description:
        description.trim().length === 0
          ? "Tell us what is happening."
          : description.trim().length < 10
            ? "A little more detail — at least 10 characters."
            : null,

      contactPhone:
        contactPhone.trim().length === 0
          ? "We need a number to reach you on."
          : normalisedPhone === null
            ? "Include the area code — for example (561) 555-0142."
            : null,

      // ⚠️ Optional, so blank is valid and only a non-empty value is checked. An extension
      // is dialled after the number answers, so letters cannot be part of one.
      contactExtension:
        contactExtension.trim().length > 0 && normaliseExtension(contactExtension) === null
          ? "Digits only, up to 8 of them."
          : null,

      // ⚠️ REQUIRED, and only askable of somebody the directory has not placed — for
      // everybody else the server takes the site off their account and this form never offers
      // the list (D-43). So the check is "answered by one of the two", not "typed here".
      //
      // Not the control, as ever: the server refuses a create it cannot settle a site for and
      // says which of the two answers is missing.
      site:
        assignedSite === null && siteId.length === 0 ? "Choose where this is coming from." : null,
    }),
    [subject, description, contactPhone, contactExtension, normalisedPhone, assignedSite, siteId],
  );

  /**
   * Which fields have been visited, so a form does not open covered in complaints.
   *
   * Nobody has got anything wrong yet when the screen first draws; marking every empty
   * required field in red before somebody has typed is a form scolding them for not having
   * finished it. A message appears once they have left the field, or once they have tried
   * to continue past it.
   */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (field: string) => setTouched((t) => ({ ...t, [field]: true }));

  const showProblem = (field: keyof typeof problems) => (touched[field] ? problems[field] : null);

  /**
   * The categories on offer, narrowed by whatever has been typed.
   *
   * Case-insensitive substring on the name. Empty search means the whole list, so the step
   * opens showing everything rather than waiting to be searched — most teams have few
   * enough categories to read, and the box is there for the ones that do not.
   */
  const visibleCategories = useMemo(() => {
    const all = team?.categories ?? [];
    const term = categorySearch.trim().toLowerCase();

    return term ? all.filter((c) => c.name.toLowerCase().includes(term)) : all;
  }, [team, categorySearch]);

  /**
   * ⚠️ The extra step is drawn from the SERVER's answer (`canRaiseForOthers`), never from
   * the permission list. Permissions reach the browser without scope, so
   * `can("ticket.create")` is true for every employee — gating on it would ask all ~29 sites
   * whose request this is and then have the create refused. Rendering only either way: the
   * endpoint re-checks the same expression, so forcing the URL yields a 403, not a ticket.
   */
  const steps = useMemo(
    () => (user?.canRaiseForOthers ? [requesterStep, ...baseSteps] : [...baseSteps]),
    [user?.canRaiseForOthers],
  );

  const currentKey: StepKey = steps[Math.min(step, steps.length - 1)].key;

  /**
   * Puts a stored draft back, once, as soon as there is a reference list to resolve it
   * against.
   *
   * ⚠️ It waits for `reference` because team and category are stored as ids and have to
   * become the objects the rest of the form holds. An id that no longer resolves — a team
   * closed or a category retired since the draft was written — is dropped and the person
   * chooses again, which is the correct outcome: the typed text is what was worth keeping.
   *
   * ⚠️ It lands on the DETAILS step when both resolved, rather than at the beginning. The
   * earlier steps are already answered, and the whole purpose of the draft is the text that
   * lives on this one. Found by key, never by index — the list is a step longer for somebody
   * who may raise a request for a colleague (see `baseSteps`).
   */
  useEffect(() => {
    if (!reference || restoreAttempted.current) return;
    restoreAttempted.current = true;

    const draft = readRequestDraft(forUser ?? null);

    // Settled either way — there was nothing to put back, and the saver may start.
    if (!draft) {
      setRestoreSettled(true);
      return;
    }

    const storedTeam = reference.teams.find((t) => t.id === draft.teamId) ?? null;
    const storedCategory = storedTeam?.categories.find((c) => c.id === draft.categoryId) ?? null;

    setTeam(storedTeam);
    setCategory(storedCategory);
    setSubject(draft.subject);
    setDescription(draft.description);
    setSiteId(draft.siteId);
    setContactPhone(draft.contactPhone);
    setContactExtension(draft.contactExtension);
    setRestoredDraft(true);

    if (storedTeam && storedCategory) {
      const details = steps.findIndex((s) => s.key === "details");
      if (details >= 0) setStep(details);
    }

    setRestoreSettled(true);
  }, [reference, forUser, steps]);

  /**
   * Keeps the draft current.
   *
   * ⚠️ Writes nothing until something has been typed, so abandoning a form after only
   * choosing a team leaves no trace to explain on the next visit — and clears itself again
   * the moment the subject and description are emptied.
   */
  useEffect(() => {
    // ⚠️ Never before the restore has had its turn — see `restoreSettled`.
    if (!restoreSettled) return;

    // Nothing to keep once it has been filed; `submit` clears the stored copy directly.
    if (created) return;

    writeRequestDraft({
      teamId: team?.id ?? "",
      categoryId: category?.id ?? "",
      subject,
      description,
      siteId,
      contactPhone,
      contactExtension,
      forUser: forUser ?? null,
    });
  }, [
    restoreSettled,
    created,
    team,
    category,
    subject,
    description,
    siteId,
    contactPhone,
    contactExtension,
    forUser,
  ]);

  /** Throws the draft away and empties the form with it. One control, doing both. */
  function discardDraft() {
    clearRequestDraft();
    setRestoredDraft(false);
    setSubject("");
    setDescription("");
    setContactPhone(forUser ? "" : formatPhone(user?.contact.contactPhone));
    setContactExtension(forUser ? "" : (user?.contact.contactPhoneExtension ?? ""));
    setTeam(null);
    setCategory(null);
    setSiteId("");
    setTouched({});
    goToStep(0);
  }

  const canContinue = useMemo(() => {
    // Each selection step gates on its OWN answer rather than on the pair, so Continue on
    // the team step no longer waits for a category chosen a screen later.
    switch (currentKey) {
      case "requester":
        // ⚠️ "Somebody else" is not an answer on its own — a name has to have been picked,
        // and it has to have resolved. An id the screen cannot put a name to stops the form
        // here rather than at submit, where the only thing left to do about it is abandon a
        // description somebody has already typed.
        if (requesterChoice === "other") return Boolean(forUser) && !onBehalfOfUnknown;

        return requesterChoice === "me";

      case "team":
        return team !== null;

      case "category":
        return category !== null;

      case "details":
        return (
          problems.subject === null &&
          problems.description === null &&
          problems.contactPhone === null &&
          problems.contactExtension === null &&
          problems.site === null
        );

      default:
        return true;
    }
  }, [currentKey, requesterChoice, forUser, onBehalfOfUnknown, team, category, problems]);

  async function submit() {
    if (!category || submitting) return;

    // ⚠️ Refuses rather than guessing. If the person this request is FOR cannot be named on
    // the screen, nothing here knows whose request is about to be created — and the one
    // thing worse than not filing it is filing it against a colleague who never asked for
    // anything. They would receive the acknowledgement, and it would count as theirs.
    if (raisingForSomeoneElse && onBehalfOfUnknown) {
      setSubmitError(
        "This request is set to be raised for somebody else, and their name could not be " +
          "looked up — so it cannot be filed against them. Raise it for yourself instead, " +
          "or ask an administrator.",
      );

      return;
    }

    setSubmitting(true);
    setSubmitStage("creating");
    setSubmitError(null);

    // ⚠️ BOTH numbers, because they do two different jobs (D-4, D-35). The readable one is
    // what the confirmation screen says and what the requester will quote on the phone; the
    // opaque one addresses the ticket and is what every call and link below is built on.
    let number: string;
    let displayNumber: number;

    try {
      const ticket = await createTicket({
        categoryId: category.id,
        subject: subject.trim(),
        description: description.trim(),
        siteId: siteId || undefined,
        idempotencyKey: idempotencyKey.current,

        // As typed. The server normalises and is the authority on the stored shape — the
        // check above only decides whether this form lets somebody continue.
        contactPhone: contactPhone.trim(),
        contactPhoneExtension: contactExtension.trim() || undefined,

        // Undefined in the ordinary case, which is the overwhelming majority. The server
        // refuses this unless the caller may create beyond their own, and records who
        // actually filed it either way.
        requesterUserId: forUser,
      });

      number = ticket.number;
      displayNumber = ticket.displayNumber;

      // ⚠️ Cleared the moment the request exists, and before the attachments and invitations
      // below — those can fail, and a failure there must not leave a draft behind that would
      // reappear as an unfiled request and get raised a second time. The screen already says
      // plainly which files or people did not make it, and both are fixable on the ticket.
      clearRequestDraft();
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : "Your request was not submitted.");
      setSubmitting(false);
      return;
    }

    // The server has just kept this number against a profile that had none. Re-read the
    // session so a second request today pre-fills — without this the screen would ask for
    // it again, which is the exact friction the saving exists to remove, and the person
    // would reasonably conclude it had not been saved.
    //
    // ⚠️ Not awaited and not allowed to fail loudly: the request is already raised, and
    // nothing below may suggest otherwise because a profile refresh did not land.
    if (!forUser && !user?.contact.contactPhone) void refresh().catch(() => undefined);

    // ⚠️ THE REQUEST EXISTS FROM HERE ON, and nothing below may suggest otherwise.
    //
    // The files are sent after it because an attachment is authorised against its parent
    // ticket — there is nothing to authorise against until now. A failure here loses a
    // file, never the request, and the person is told which.
    const failures: { name: string; reason: string }[] = [];

    if (files.length > 0) {
      setSubmitStage("attaching");

      // One at a time, not in parallel. The per-request total is evaluated server-side
      // against what is already stored, so concurrent uploads can each see a total that
      // excludes the others and both pass a check neither should. Sequential keeps the
      // cap exact, and a handful of small files is not worth racing.
      for (const file of files) {
        try {
          await uploadAttachment(number, file);
        } catch (e) {
          failures.push({
            name: file.name,
            reason: e instanceof ApiError ? e.message : "That file could not be attached.",
          });
        }
      }
    }

    setAttachmentFailures(failures);
    setAttachedCount(files.length - failures.length);

    // ⚠️ Invitations, after the ticket and for the same reason as the files: an invitation
    // is authorised against its parent ticket, so there was nothing to authorise against
    // until now. Every guard runs on that endpoint — the site rule, category clearance, the
    // audit event — and a refusal here loses one invitation, never the request.
    const refused: { name: string; reason: string }[] = [];

    if (participants.length > 0) {
      setSubmitStage("inviting");

      for (const person of participants) {
        try {
          await addParticipant(number, person.userId);
        } catch (e) {
          refused.push({
            name: person.displayName,
            // The server explains a refusal in terms a person can act on — "they do not
            // have access to this category of request". Verbatim beats "could not add".
            reason: e instanceof ApiError ? e.message : "They could not be added.",
          });
        }
      }
    }

    setParticipantFailures(refused);
    setInvitedCount(participants.length - refused.length);

    setCreated({ number, displayNumber });
    setSubmitting(false);
  }

  if (created) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center sm:px-6">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" aria-hidden />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">
          {/* ⚠️ Whose request this is, on the last screen as well as the first. "Request
              submitted / your reference is" reads as your own however it was filed, and
              somebody who did not mean to raise it for a colleague had nothing here to
              tell them otherwise. */}
          {raisingForSomeoneElse
            ? `Request submitted for ${onBehalfOf?.displayName ?? "somebody else"}`
            : "Request submitted"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {/* ⚠️ The READABLE number, which is what the queue, the emails, the palette and
              every notification say (D-35). The opaque one still addresses the request and
              is what the button below navigates on — it is simply not what anybody quotes. */}
          {raisingForSomeoneElse ? "The" : "Your"} reference is{" "}
          <span className="font-medium">#{created.displayNumber}</span>.{" "}
          {raisingForSomeoneElse
            ? `It belongs to ${onBehalfOf?.displayName ?? "them"} — they have been told it was received, and it counts as theirs. The ticket records that you raised it.`
            : "We'll be in touch through the service desk."}
          {attachedCount > 0 &&
            ` ${attachedCount} ${attachedCount === 1 ? "file" : "files"} attached.`}
          {invitedCount > 0 &&
            ` ${invitedCount} ${invitedCount === 1 ? "person" : "people"} added.`}
        </p>

        {attachmentFailures.length > 0 && (
          // The request went in. Only the files did not, and each one says why — a person
          // who is told "something went wrong" raises the whole request again.
          <div
            role="alert"
            className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-left text-sm"
          >
            <p className="font-medium">
              Your request was created, but{" "}
              {attachmentFailures.length === 1
                ? "one file was not attached"
                : `${attachmentFailures.length} files were not attached`}
              :
            </p>
            <ul className="mt-1.5 space-y-1">
              {attachmentFailures.map((f) => (
                <li key={f.name}>
                  <span className="font-medium">{f.name}</span> — {f.reason}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-muted-foreground">
              You can attach files to the request itself at any time.
            </p>
          </div>
        )}

        {/* Its own block rather than merged with the files above: a file that bounced and a
            colleague who may not see this category are different problems with different
            remedies, and each reason is the server's own words. */}
        {participantFailures.length > 0 && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-left text-sm"
          >
            <p className="font-medium">
              {participantFailures.length === 1
                ? "One person was not added"
                : `${participantFailures.length} people were not added`}
              :
            </p>
            <ul className="mt-1.5 space-y-1">
              {participantFailures.map((f) => (
                <li key={f.name}>
                  <span className="font-medium">{f.name}</span> — {f.reason}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-muted-foreground">
              You can add people to the request itself at any time.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate({ to: "/tickets/$id", params: { id: created.number } })}
            className="h-10 rounded-lg bg-gradient-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            View request
          </button>
          {/* ⚠️ Not "all my requests" when it is not theirs. The request that was just
              raised belongs to somebody else and will not be in this list — a button
              promising to show it alongside the others sends the person who filed it
              looking for a row that is not there. */}
          <Link
            to="/tickets"
            className="inline-flex h-10 items-center rounded-lg border border-input px-4 text-sm transition hover:bg-accent"
          >
            {raisingForSomeoneElse ? "Back to requests" : "All my requests"}
          </Link>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {loadError}
        </div>
      </div>
    );
  }

  if (!reference) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6" aria-busy="true">
        <div className="h-8 w-56 animate-pulse rounded bg-muted" />
        <div className="mt-6 h-64 animate-pulse rounded-2xl border border-border bg-card" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {forUser ? "Raise a request for someone" : "Get help"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {forUser
            ? "Fill this in as though they were describing it."
            : "Tell us what you need and we'll route it to the right team."}
        </p>
      </header>

      {/* ⚠️ SAID OUT LOUD, never restored silently, and the reason is the machine this runs
          on. A form that quietly refills itself is confusing anywhere; at a shared clinical
          workstation somebody has to be able to see that text is there and throw it away in
          one click — without reading it first to work out whether it is theirs.

          It also covers the ordinary case, which is somebody who does not remember leaving
          this open and would otherwise wonder why the desk knows what they were going to say. */}
      {restoredDraft && (
        <div
          role="status"
          className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
        >
          <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1">
            We kept what you had already typed. Nothing has been sent to the service desk yet.
          </span>

          <button
            type="button"
            onClick={discardDraft}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-xs transition hover:bg-accent"
          >
            <Trash2 className="h-3 w-3" aria-hidden />
            Start again
          </button>
        </div>
      )}

      {/* ⚠️ Named on screen, not just carried in the URL. Filing a request "for" somebody
          without saying who is how the wrong person ends up with a ticket - and the
          request will genuinely be theirs: they see it, they are notified, it counts as
          theirs. The ticket also records that you filed it, and says so. */}
      {raisingForSomeoneElse && (
        <div
          className={cn(
            "mt-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
            onBehalfOfUnknown
              ? "border-destructive/40 bg-destructive/10"
              : "border-primary/30 bg-primary/5",
          )}
        >
          <UserPlus
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              onBehalfOfUnknown ? "text-destructive" : "text-primary",
            )}
            aria-hidden
          />
          <div>
            {/* ⚠️ An id with no name is refused rather than filed. The lookup needs
                `user.manage`, so somebody who may raise on another's behalf without it used
                to see "the person you selected" and file it anyway — against a colleague
                this screen could not name. */}
            {onBehalfOfUnknown ? (
              <>
                <p className="font-medium">This request cannot be raised for that person</p>
                <p className="mt-0.5 text-xs">
                  The link says it is for somebody else, and their name could not be looked up.
                  Raise it for yourself with the link below, or ask an administrator.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">
                  This request will belong to {onBehalfOf?.displayName ?? "somebody else"}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  They will see it and be notified about it — it counts as theirs, not yours. The
                  ticket will record that you raised it on their behalf.
                </p>
              </>
            )}

            {/* ⚠️ The way out, on the screen rather than in the URL. `forUser` survives a
                bookmark, the back button and the address bar's autocomplete, so somebody who
                once raised a request for a colleague can land here days later with their own
                problem to report. */}
            <Link
              to="/new"
              search={{}}
              className="mt-1 inline-block text-xs font-medium underline underline-offset-2"
            >
              This one is for me
            </Link>
          </div>
        </div>
      )}

      {/* Progress. Labels are hidden on a phone where three of them do not fit; the
          numbered dots still show where you are. */}
      <ol className="mt-6 flex items-center gap-2" aria-label="Progress">
        {steps.map(({ key, label }, i) => (
          <li key={key} className="flex flex-1 items-center gap-2">
            {/*
              A completed step is a button; the current and future ones are not.

              Going back is always safe — the answers are still in state and nothing is
              submitted until the last step. Jumping FORWARD is not, because each step
              gates on the one before it, so those stay inert rather than being rendered
              as buttons that refuse to work.

              type="button" matters: this sits inside a form, and the default submit type
              would post the request from the progress bar.
            */}
            <button
              type="button"
              onClick={() => i < step && goToStep(i)}
              disabled={i >= step}
              aria-current={i === step ? "step" : undefined}
              aria-label={i < step ? `Back to ${label}` : label}
              className={cn(
                "grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold transition",
                i < step
                  ? "bg-success text-success-foreground hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  : i === step
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                // Not disabled:opacity — the current step must stay fully legible.
                i >= step && "cursor-default",
              )}
            >
              {i + 1}
            </button>
            <span
              className={cn(
                "hidden text-sm sm:block",
                i === step ? "font-medium" : "text-muted-foreground",
              )}
            >
              {label}
            </span>
            {i < steps.length - 1 && <span className="h-px flex-1 bg-border" aria-hidden />}
          </li>
        ))}
      </ol>

      {/*
        The carousel viewport.

        overflow-hidden is what makes the movement read as one panel sliding aside for the
        next rather than content jumping — the outgoing edge is clipped by the card border
        instead of spilling across the page.

        Only the active panel is mounted, and `key={step}` is what drives the animation:
        React replaces the node on every step change, so the entry animation runs each time.
        Mounting all three and translating a track would be the textbook carousel, but each
        step here is a different height, so the card would always stand as tall as the
        tallest one and short steps would sit in a pool of empty space.

        motion-reduce:animate-none — a horizontal slide is exactly the vestibular trigger
        the prefers-reduced-motion setting exists for. The step still changes; it simply
        appears.
      */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
        <div
          key={step}
          className={cn(
            "p-4 duration-300 ease-out animate-in fade-in motion-reduce:animate-none sm:p-6",
            direction === 1 ? "slide-in-from-right-8" : "slide-in-from-left-8",
          )}
        >
          {currentKey === "requester" && (
            <fieldset>
              <legend className="text-sm font-medium">Who is this request for?</legend>
              <p className="mt-1 text-sm text-muted-foreground">
                Raising it for somebody else makes the request theirs — they see it, they are
                notified about it, and it counts as theirs. The ticket records that you filed it.
              </p>

              {/* ⚠️ TWO BUTTONS, not one button and a label. Both are choices, so both have
                  to be pressable — an inert card sitting beside a live one reads as a
                  disabled option, and the only way left to answer is to search, which is the
                  step nobody reaches if they have not first said that is what they are
                  doing. */}
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    // ⚠️ Clears `forUser` from the URL rather than only from state. Every
                    // guard downstream — the banner, the blanked phone field, the site, the
                    // submit button's wording — reads the search param, so leaving it set
                    // while the screen says "for me" would file it against the colleague.
                    setRequesterChoice("me");
                    if (forUser) void navigate({ to: "/new", search: {}, replace: true });
                  }}
                  aria-pressed={requesterChoice === "me"}
                  className={cn(
                    "rounded-xl border p-3 text-left transition",
                    requesterChoice === "me"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40",
                  )}
                >
                  <span className="block font-medium">It is mine</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    The usual case — you are reporting your own problem.
                  </span>
                </button>

                <button
                  type="button"
                  // Does NOT clear a person already chosen: coming back to this step and
                  // pressing the option you are already on should not undo the answer.
                  onClick={() => setRequesterChoice("other")}
                  aria-pressed={requesterChoice === "other"}
                  className={cn(
                    "rounded-xl border p-3 text-left transition",
                    requesterChoice === "other"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40",
                  )}
                >
                  <span className="block font-medium">It is for somebody else</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Somebody rang who cannot sign in.
                  </span>
                </button>
              </div>

              {/* ⚠️ Revealed by the choice rather than shown always. A staff search sitting
                  open on a form somebody opened to report their own problem invites a
                  colleague's name into a request that is not theirs. */}
              {requesterChoice === "other" && (
                <div className="mt-4">
                  {/* ⚠️ Selecting writes `forUser` into the URL rather than into local state,
                      on purpose. Everything that makes an on-behalf request safe already
                      hangs off that param — a second source of truth in component state
                      would be a second thing to keep in step, and the one that drifts is
                      always the one nobody is looking at. */}
                  <RequesterPicker
                    value={onBehalfOf}
                    onChange={(p) =>
                      void navigate({ to: "/new", search: { forUser: p.userId }, replace: true })
                    }
                    disabled={submitting}
                  />

                  {/* Who is currently chosen, stated rather than left to the highlight on a
                      row that scrolls out of view as soon as the next search runs. */}
                  {onBehalfOf && (
                    <p className="mt-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                      This request will belong to{" "}
                      <span className="font-medium">{onBehalfOf.displayName}</span>.
                    </p>
                  )}

                  {!onBehalfOf && !onBehalfOfUnknown && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Find them above to continue.
                    </p>
                  )}
                </div>
              )}
            </fieldset>
          )}

          {currentKey === "team" && (
            <fieldset>
              <legend className="text-sm font-medium">Which team can help?</legend>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {reference.teams.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      // ⚠️ Changing the team clears the category rather than keeping it.
                      // A category belongs to exactly one team and carries the sensitivity
                      // that decides who may read the request, so a stale one would file
                      // the ticket into the team it came from — not the one on screen.
                      setTeam(t);
                      setCategory(null);
                      setCategorySearch("");
                    }}
                    className={cn(
                      "rounded-xl border p-3 text-left transition",
                      team?.id === t.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    <span className="block font-medium">{t.name}</span>
                    {t.description && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {t.description}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {currentKey === "category" && (
            <fieldset>
              <legend className="text-sm font-medium">What is it about?</legend>
              <p className="mt-1 text-sm text-muted-foreground">
                {team?.name} handles these. Pick the closest one — the desk re-routes it if you are
                not sure.
              </p>

              {/*
                ⚠️ Filtered in the browser over a list already in hand. Categories arrive
                with the reference data the form fetched on load, so there is no endpoint
                here, no request per keystroke, and nothing about a category is anybody's
                personal data.

                Matches the name only, because a name is all a category has — there is no
                description column on the entity. Worth knowing if this ever feels blunt:
                the fix is a description on Category, not a cleverer filter here.
              */}
              {visibleCategories.length > 0 && (
                <div className="relative mt-3">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <input
                    id="category-search"
                    type="search"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="Search categories…"
                    aria-label="Search categories"
                    className="h-10 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-base focus:border-ring focus:outline-none md:text-sm"
                  />
                </div>
              )}

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {visibleCategories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c)}
                    className={cn(
                      "flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition",
                      category?.id === c.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    <span className="min-w-0">{c.name}</span>

                    {/* Shown so a person knows a sensitive matter is handled as one,
                      before they type it. It does not restrict who may raise it —
                      the people who most need Employee Relations are exactly those
                      with no special clearance. */}
                    {c.sensitivity !== "Standard" && (
                      <Lock className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                    )}
                  </button>
                ))}
              </div>

              {/* ⚠️ Three empties, said apart. "No categories at all" is an administrator's
                  job, "nothing matches what you typed" is a word to change, and they look
                  identical as a blank space under a list. */}
              {team && team.categories.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  This team has no categories set up yet. Pick another team, or ask the service
                  desk.
                </p>
              )}

              {team && team.categories.length > 0 && visibleCategories.length === 0 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Nothing in {team.name} matches “{categorySearch.trim()}”.{" "}
                  <button
                    type="button"
                    onClick={() => setCategorySearch("")}
                    className="font-medium underline underline-offset-2"
                  >
                    Show all categories
                  </button>
                </p>
              )}
            </fieldset>
          )}

          {currentKey === "details" && (
            <div className="space-y-4">
              {/* Only when the flag is on — and it is locked off until the Azure OpenAI
                abuse-monitoring exemption is asserted in deployment config (D-5). The
                endpoint 404s independently, so this is presentation, not the gate. */}
              {aiEnabled && (
                <AssistantPanel
                  onDraft={(draft) => {
                    // Fills the fields the person is about to edit. Nothing is submitted,
                    // and the category only moves if the SERVER matched a real one that
                    // belongs to the team already chosen — the model never picks a record.
                    setSubject(draft.subject.slice(0, 200));
                    setDescription(draft.description.slice(0, 4000));

                    if (draft.categoryId) {
                      const match = team?.categories.find((c) => c.id === draft.categoryId);
                      if (match) setCategory(match);
                    }
                  }}
                />
              )}

              <div>
                <label htmlFor="subject" className="text-sm font-medium">
                  Short summary <RequiredMark />
                </label>
                <input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onBlur={() => touch("subject")}
                  maxLength={200}
                  required
                  aria-required="true"
                  aria-invalid={showProblem("subject") !== null}
                  aria-describedby="subject-problem"
                  placeholder="Can't sign in to the EMR"
                  className={cn(
                    "mt-1 h-10 w-full rounded-lg border bg-background px-3 text-base focus:outline-none md:text-sm",
                    showProblem("subject")
                      ? "border-destructive focus:border-destructive"
                      : "border-input focus:border-ring",
                  )}
                />
                <FieldProblem id="subject-problem" message={showProblem("subject")} />
              </div>

              <div>
                <label htmlFor="description" className="text-sm font-medium">
                  What's happening? <RequiredMark />
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => touch("description")}
                  rows={6}
                  maxLength={4000}
                  required
                  aria-required="true"
                  aria-invalid={showProblem("description") !== null}
                  aria-describedby="description-problem description-phi"
                  placeholder="What you were doing, what happened, and anything you've already tried."
                  className={cn(
                    "mt-1 w-full resize-y rounded-lg border bg-background p-3 text-base focus:outline-none md:text-sm",
                    showProblem("description")
                      ? "border-destructive focus:border-destructive"
                      : "border-input focus:border-ring",
                  )}
                />
                <FieldProblem id="description-problem" message={showProblem("description")} />

                {/* Said plainly, where it is relevant, rather than in a policy nobody
                  reads. People paste patient details into free text regardless — this
                  at least gives them the chance not to. */}
                <p id="description-phi" className="mt-1.5 text-xs text-muted-foreground">
                  Please don't include patient names or health information. A record number is
                  enough for us to find it.
                </p>
              </div>

              {/* Beside the description, because a screenshot is part of describing the
                  problem — not an afterthought performed on a ticket that already exists.
                  The files ride along in the browser and are sent the moment the request
                  has an id to attach them to. */}
              {attachmentsEnabled && (
                <PendingFilesField files={files} onChange={setFiles} disabled={submitting} />
              )}

              {/*
                How to reach them. Required, because half of resolving a request is ringing
                the person back, and a ticket with no way to do that becomes a message left
                in a queue neither side is watching.

                ⚠️ Asked on the form rather than read from the directory, and the two are
                not the same fact. The directory holds where somebody's desk is; this asks
                where they are for THIS problem — a ward extension, a colleague's phone, a
                mobile because they are between sites. A request is answered hours later by
                somebody who cannot ask.
              */}
              <div>
                <span className="text-sm font-medium">How can we reach you?</span>

                <div className="mt-1 flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <label htmlFor="contact-phone" className="text-xs text-muted-foreground">
                      Phone number <RequiredMark />
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      onBlur={() => touch("contactPhone")}
                      required
                      aria-required="true"
                      aria-invalid={showProblem("contactPhone") !== null}
                      aria-describedby="contact-phone-problem contact-phone-help"
                      placeholder="(561) 555-0142"
                      className={cn(
                        "mt-1 h-10 w-full rounded-lg border bg-background px-3 text-base focus:outline-none md:text-sm",
                        showProblem("contactPhone")
                          ? "border-destructive focus:border-destructive"
                          : "border-input focus:border-ring",
                      )}
                    />
                  </div>

                  <div className="w-28">
                    {/* ⚠️ Labelled optional on the control itself. A field beside a required
                        one reads as required unless it says otherwise, and somebody with no
                        extension should not be hunting for what to put in it. */}
                    <label htmlFor="contact-extension" className="text-xs text-muted-foreground">
                      Extension <span className="text-muted-foreground/70">(optional)</span>
                    </label>
                    <input
                      id="contact-extension"
                      inputMode="numeric"
                      value={contactExtension}
                      onChange={(e) => setContactExtension(e.target.value)}
                      onBlur={() => touch("contactExtension")}
                      aria-invalid={showProblem("contactExtension") !== null}
                      aria-describedby="contact-extension-problem"
                      placeholder="4021"
                      className={cn(
                        "mt-1 h-10 w-full rounded-lg border bg-background px-3 text-base focus:outline-none md:text-sm",
                        showProblem("contactExtension")
                          ? "border-destructive focus:border-destructive"
                          : "border-input focus:border-ring",
                      )}
                    />
                  </div>
                </div>

                <FieldProblem id="contact-phone-problem" message={showProblem("contactPhone")} />
                <FieldProblem
                  id="contact-extension-problem"
                  message={showProblem("contactExtension")}
                />

                {/* The helper text, which is about what happens to the number rather than
                    about what is wrong with it — those are two different messages and
                    showing one in place of the other loses whichever it replaced. */}
                <p id="contact-phone-help" className="mt-1.5 text-xs text-muted-foreground">
                  {forUser
                    ? "Where the service desk should ring them about this request."
                    : // Said before it happens rather than discovered afterwards. The
                      // server only fills an empty field and never overwrites one.
                      user?.contact.contactPhone
                      ? "From your profile. Changing it here applies to this request only."
                      : "We'll save this to your profile so you don't have to type it again."}
                </p>
              </div>

              {/*
                ⚠️ Chosen here and invited after the request exists, because an invitation is
                authorised against a parent ticket and there is none yet. Selecting somebody
                grants nothing: the site rule, category clearance and the audit event all run
                on the invitation itself, and a refusal is reported on the confirmation screen.
              */}
              <PendingParticipantsField
                people={participants}
                onChange={setParticipants}
                disabled={submitting}
              />

              {/*
                Where they are — asked ONLY when nobody has already answered it.

                ⚠️ Directory sync or an administrator has placed most people, and that
                placement is the answer. Offering the list to somebody already placed invites
                a different one, and a request filed against the wrong site is routed and
                reported against the wrong site for the rest of its life.

                ⚠️ The server derives this from the requester's record regardless of what
                this form posts, so hiding the picker is a courtesy and never the control.
                Somebody with no site keeps the choice, which is the case it exists for.
              */}
              {assignedSite ? (
                <div>
                  <span className="text-sm font-medium">Where are you?</span>
                  <p className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    {assignedSite}
                  </p>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {forUser
                      ? "From their account. Ask an administrator if it is wrong."
                      : "From your account. Ask the service desk if it is wrong."}
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="site" className="text-sm font-medium">
                    {forUser ? "Where are they?" : "Where are you?"}
                  </label>

                  {/* Searchable: twenty-nine sites is past the number anybody scans, and a
                      list chosen from by scrolling until something looks close is how a
                      request lands against the wrong facility.

                      ⚠️ `required` removes the "Not site-specific" option rather than
                      leaving it there to be refused on submit. A list that offers an answer
                      the form will not accept is a worse form than one that does not offer
                      it, and this is the only screen where the choice is asked for. */}
                  <SiteCombobox
                    id="site"
                    sites={reference.sites}
                    value={siteId}
                    onChange={(chosen) => {
                      setSiteId(chosen);
                      touch("site");
                    }}
                    disabled={submitting}
                    required
                    invalid={showProblem("site") !== null}
                  />

                  {showProblem("site") ? (
                    <p className="mt-1.5 text-xs text-destructive">{showProblem("site")}</p>
                  ) : (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {forUser
                        ? "Their account has no site recorded, so this request needs one."
                        : "Your account has no site recorded, so this request needs one."}{" "}
                      It decides which desk sees this and how it is reported.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {currentKey === "review" && (
            <div className="space-y-4">
              <h2 className="text-sm font-medium">Check this over</h2>

              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Team</dt>
                  <dd>
                    {team?.name} · {category?.name}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Summary</dt>
                  <dd className="font-medium">{subject}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Details</dt>
                  <dd className="whitespace-pre-wrap">{description}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Contact</dt>
                  {/* Shown normalised, so somebody sees the number as it will be stored and
                      can catch a transposed digit here rather than by not being rung. */}
                  <dd>{formatPhone(normalisedPhone, contactExtension.trim() || null)}</dd>
                </div>
                {/* ⚠️ Always drawn, never conditional. A request has a site (D-100), so a
                    review that silently omitted the row would be hiding the one field on this
                    form somebody cannot correct once it is filed. */}
                <div>
                  <dt className="text-muted-foreground">Site</dt>
                  <dd>
                    {assignedSite ??
                      (() => {
                        const chosen = reference.sites.find((s) => s.id === siteId);
                        return chosen ? formatSite(chosen) : "Not chosen";
                      })()}
                  </dd>
                </div>
                {participants.length > 0 && (
                  <div>
                    <dt className="text-muted-foreground">Also on this request</dt>
                    <dd>{participants.map((p) => p.displayName).join(", ")}</dd>
                  </div>
                )}
                {files.length > 0 && (
                  <div>
                    <dt className="text-muted-foreground">Files</dt>
                    <dd>
                      <ul>
                        {files.map((f) => (
                          <li key={`${f.name}-${f.size}`}>
                            {f.name}{" "}
                            <span className="text-muted-foreground">({formatBytes(f.size)})</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
              </dl>

              {submitError && (
                <div
                  role="alert"
                  className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
                >
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Full-width buttons on a phone, inline above it. */}
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={() => goToStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-input px-4 text-sm transition hover:bg-accent disabled:opacity-40 sm:h-10"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back
        </button>

        {step < steps.length - 1 ? (
          /*
            ⚠️ NOT disabled when the step is incomplete, and that is the point of the
            change. A greyed-out Continue is a dead end that says nothing: the person cannot
            see which field is holding them up, and the commonest outcome is that they
            abandon the request rather than hunt for it. Pressing it marks every field as
            visited, which reveals each message at once, and refuses to advance.

            The two SELECTION steps keep a disabled button — there is exactly one thing to
            do on each and the unselected cards say so themselves, so a press has no hidden
            message to reveal.
          */
          <button
            type="button"
            onClick={() => {
              if (canContinue) {
                goToStep(step + 1);
                return;
              }

              setTouched({
                subject: true,
                description: true,
                contactPhone: true,
                contactExtension: true,
                site: true,
              });
            }}
            disabled={currentKey !== "details" && !canContinue}
            aria-disabled={!canContinue}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-gradient-primary px-5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50 sm:h-10"
          >
            Continue <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-gradient-primary px-5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50 sm:h-10"
          >
            <Send className="h-4 w-4" aria-hidden />
            {/* ⚠️ The button says whose request this will be. A banner at the top of a
                three-step wizard is read once, on step one, by somebody choosing a
                category; the last thing anyone reads before submitting is the button they
                are about to press. */}
            {!submitting
              ? raisingForSomeoneElse
                ? `Submit for ${onBehalfOf?.displayName ?? "somebody else"}`
                : "Submit request"
              : submitStage === "attaching"
                ? "Attaching files…"
                : submitStage === "inviting"
                  ? "Adding people…"
                  : "Submitting…"}
          </button>
        )}
      </div>
    </div>
  );
}
